'use client';

import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx/lite';
import { useMemo, useState } from 'react';
import { CaretIcon } from '~/components/Icons';
import { STATS_CONFIG } from '~/config/statsConfig';
import statsMetaData from '~/data/statsMetaData.json';
import { getColumnMetadata } from '~/utils/getColumnMetadata';
import { Container } from '../../Container';

// Modify STATS_CONFIG for splits to use duration instead of year
const BATTING_SPLITS_CONFIG = {
  ...STATS_CONFIG.batting,
  columns: STATS_CONFIG.batting.columns
    .filter((col) => !['gamesPlayed', 'gamesStarted'].includes(col.accessor))
    .map((col) =>
      col.accessor === 'year'
        ? { ...col, accessor: 'duration', header: 'Duration' }
        : col,
    ),
};

// Memoize column creation
const columnHelper = createColumnHelper();
const createColumns = (columns, statsMetaData) => {
  // Map columns with their metadata
  const columnsWithMeta = columns.map((col) => {
    const metadata = getColumnMetadata(
      col.accessor,
      statsMetaData,
      col.statTableId,
    );

    return {
      columnDef: columnHelper.accessor(col.accessor, {
        header: metadata?.headerText || col.header,
        cell: (info) => {
          const value = info.getValue();

          // Handle null/undefined values
          if (value === null || value === undefined || value === 0) {
            return <div>-</div>;
          }

          // Handle special split column
          if (col.accessor === 'split') {
            return <div>{value}</div>;
          }

          // Apply precision formatting if specified in metadata
          if (metadata?.precision !== undefined && typeof value === 'number') {
            const formatted = value.toFixed(metadata.precision);
            return (
              <div>
                {metadata.leadingZeroFlg
                  ? formatted
                  : formatted.replace(/^0/, '')}
              </div>
            );
          }

          return <div>{value}</div>;
        },
        sortingFn: customSortingFn,
        enableSorting: true,
        sortDescFirst: true,
        meta: {
          ...col.meta,
          tooltip: metadata?.toolTipText,
          isPercentage: metadata?.percentFlg,
          seqNo: metadata?.seqNo || 999, // Use metadata seqNo for sorting
        },
      }),
      seqNo: metadata?.seqNo || 999, // Store seqNo for sorting columns
      isFirst: col.accessor === 'duration' || col.accessor === 'split', // Special case for duration column
    };
  });

  // Always put duration/split column first, then sort rest by seqNo
  const firstColumn = columnsWithMeta.find((col) => col.isFirst);
  const otherColumns = columnsWithMeta
    .filter((col) => !col.isFirst)
    .sort((a, b) => a.seqNo - b.seqNo);

  // Return the sorted columnDefs
  return firstColumn
    ? [firstColumn.columnDef, ...otherColumns.map((col) => col.columnDef)]
    : otherColumns.map((col) => col.columnDef);
};

// Custom sorting function
const customSortingFn = (rowA, rowB, columnId) => {
  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);

  // Special handling for duration column
  if (columnId === 'duration') {
    // Extract numbers from "Last X games" format
    const getGameNumber = (str) => {
      const match = str.match(/Last (\d+) games/);
      return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
    };

    const numA = getGameNumber(a);
    const numB = getGameNumber(b);

    // Sort by the numeric value (smaller numbers first)
    return numA - numB;
  }

  // Handle numeric values
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  // Handle string values
  return (a ?? '').toString().localeCompare((b ?? '').toString());
};

// Table component
const StatsTable = ({ table }) => (
  <div className="relative overflow-x-auto">
    <table className="-my-1 min-w-full border-separate border-spacing-y-1 text-left text-[18px] font-semibold leading-9 tracking-[1.5px]">
      <thead className="bg-[--theme-secondary]">
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                className={clsx(
                  'cursor-pointer select-none whitespace-nowrap px-3 py-3 font-nova text-lg font-semibold uppercase leading-tight tracking-wider text-[--theme-action]',
                  header.column.id === 'duration' &&
                    'sticky left-0 z-10 bg-[--theme-secondary]',
                )}
                onClick={header.column.getToggleSortingHandler()}
              >
                <div className="flex items-center gap-x-2">
                  {header.column.columnDef.header}
                  <CaretIcon
                    className={clsx(
                      'w-3',
                      !header.column.getIsSorted()
                        ? 'invisible'
                        : header.column.getIsSorted() === 'asc'
                          ? 'rotate-180'
                          : null,
                    )}
                  />
                </div>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id} className="border-b-2 border-white bg-[#f0f0f0]">
            {row.getVisibleCells().map((cell) => (
              <td
                key={cell.id}
                className={clsx(
                  'whitespace-nowrap px-3 py-3 font-nova text-lg font-semibold leading-tight tracking-wider text-black',
                  cell.column.id === 'duration' &&
                    'sticky left-0 z-10 bg-[#f0f0f0]',
                )}
              >
                {cell.column.columnDef.cell(cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export function PlayerSplitsTable({ data }) {
  const [sorting, setSorting] = useState([{ id: 'duration', desc: false }]);

  // Transform battingStats to include a 'duration' property based on statKey
  const stats = useMemo(() => {
    // Process each player object in the data array
    return (
      data
        .flatMap((player) => {
          const battingStats = player?.battingStats ?? [];

          // Process each batting stat for this player
          return battingStats.map((stat) => {
            // Extract duration from statKey (e.g., "3_LAST6" → "Last 6 games")
            let duration = '';
            if (stat.statKey && stat.statKey.includes('LAST')) {
              const match = stat.statKey.match(/LAST(\d+)/);
              if (match) {
                duration = `Last ${match[1]} games`;
              }
            } else if (stat.statType && stat.statType.includes('Last')) {
              // Alternative check if statType contains "Last"
              duration = stat.statType;
            }

            return {
              duration,
              ...stat,
              // Add player info if needed for display
              playerName: `${player.firstName} ${player.lastName}`,
              playerId: player.playerId,
            };
          });
        })
        // Filter out any stats that don't have duration (non-splits)
        .filter((stat) => stat.duration)
        // Remove any undefined or null entries
        .filter(Boolean)
    );
  }, [data]);

  // Memoize active configuration
  const activeConfig = useMemo(() => {
    // Add statTableId to columns that don't have one
    const columnsWithTableId = BATTING_SPLITS_CONFIG.columns.map((col) => ({
      ...col,
      statTableId:
        col.statTableId !== undefined
          ? col.statTableId
          : BATTING_SPLITS_CONFIG.tableId,
    }));

    return {
      data: stats,
      columns: createColumns(columnsWithTableId, statsMetaData),
    };
  }, [stats]);

  // Memoize table instance
  const table = useReactTable({
    data: stats,
    columns: activeConfig.columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
  });

  return (
    <Container className="gap-6 py-5">
      <div className="flex items-start justify-start gap-9 self-stretch">
        <h2 className="shrink grow basis-0 font-nova text-4xl font-black uppercase text-black">
          Splits
        </h2>
      </div>
      <StatsTable table={table} />
    </Container>
  );
}
